export class NADStickyAddToCart {
  private element: HTMLElement;
  private variantEl: HTMLElement | null;
  private priceEl: HTMLElement | null;
  private compareEl: HTMLElement | null;
  private submitBtn: HTMLButtonElement | null;
  private buttonTextEl: HTMLElement | null;
  private observer: IntersectionObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private rechargeObservers: MutationObserver[] = [];
  private sellingPlanPollId: ReturnType<typeof setInterval> | null = null;
  private buttonLabel: string;
  private countdownEl: HTMLElement | null;
  private countdownEndMs: number = 0;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  constructor(element: HTMLElement) {
    this.element = element;
    this.variantEl = element.querySelector('[data-sticky-atc-variant]');
    this.priceEl = element.querySelector('[data-sticky-atc-price]');
    this.compareEl = element.querySelector('[data-sticky-atc-compare]');
    this.submitBtn = element.querySelector('[data-sticky-atc-submit]');
    this.buttonTextEl = element.querySelector('[data-sticky-atc-button-text]');
    this.buttonLabel = this.buttonTextEl?.textContent?.trim() || 'Try it now';
    this.countdownEl = element.querySelector('[data-sticky-atc-countdown]');
    this.init();
  }

  private init(): void {
    this.element.style.display = '';
    this.bindSubmit();
    this.observeMainForm();
    this.listenVariantChanges();
    this.listenSellingPlanChanges();
    this.observePriceContainer();
    this.observeRechargeWidget();
    this.startCountdown();
  }

  private startCountdown(): void {
    if (!this.countdownEl) return;
    const endSec = parseInt(this.countdownEl.getAttribute('data-countdown-end') || '0', 10);
    this.countdownEndMs = Number.isFinite(endSec) ? endSec * 1000 : 0;
    if (this.countdownEndMs <= 0) return;
    this.tickCountdown();
    this.countdownTimer = setInterval(() => this.tickCountdown(), 1000);
  }

  private tickCountdown(): void {
    if (!this.countdownEl) return;
    let remaining = Math.floor((this.countdownEndMs - Date.now()) / 1000);
    if (remaining <= 0) {
      this.countdownEl.textContent = '00:00:00';
      if (this.countdownTimer != null) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
      return;
    }

    const days = Math.floor(remaining / 86400);
    remaining -= days * 86400;
    const hours = Math.floor(remaining / 3600);
    remaining -= hours * 3600;
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining - minutes * 60;

    const pad = (n: number): string => n.toString().padStart(2, '0');
    let out = '';
    if (days > 0) out += `${days} ${days === 1 ? 'day' : 'days'} `;
    out += `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    this.countdownEl.textContent = out;
  }

  private bindSubmit(): void {
    this.submitBtn?.addEventListener('click', () => {
      const mainForm = document.querySelector<HTMLFormElement>(
        'product-form form[data-type="add-to-cart-form"]'
      );
      if (!mainForm) return;

      const mainSubmit = mainForm.querySelector<HTMLButtonElement>('[type="submit"]');
      if (mainSubmit && !mainSubmit.disabled) {
        mainSubmit.click();
      }
    });
  }

  private observeMainForm(): void {
    const waitForForm = (retries = 30): void => {
      const formContainer = document.querySelector('product-form');
      if (formContainer) {
        this.setupIntersectionObserver(formContainer as HTMLElement);
        return;
      }
      if (retries > 0) setTimeout(() => waitForForm(retries - 1), 100);
    };
    waitForForm();
  }

  private setupIntersectionObserver(target: HTMLElement): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.element.classList.remove('nad-sticky-atc--visible');
            this.setStickyOffsetProperty(false);
          } else {
            this.element.classList.add('nad-sticky-atc--visible');
            this.setStickyOffsetProperty(true);
          }
        });
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this.observer.observe(target);
  }

  private setStickyOffsetProperty(visible: boolean): void {
    // Exposed so third-party bottom-anchored widgets (e.g. Shopify Inbox chat
    // bubble) can clear the bar; consumed in nad-sticky-add-to-cart.scss.
    const root = document.documentElement;
    if (visible && this.element.offsetHeight > 0) {
      root.style.setProperty('--nad-sticky-atc-offset', `${this.element.offsetHeight + 8}px`);
    } else {
      root.style.removeProperty('--nad-sticky-atc-offset');
    }
  }

  private listenVariantChanges(): void {
    const waitForInput = (retries = 30): void => {
      const variantInput = document.querySelector<HTMLInputElement>(
        'input[name="id"].product-variant-id'
      );
      if (variantInput) {
        variantInput.addEventListener('change', () => this.syncFromDOM());
        return;
      }
      if (retries > 0) setTimeout(() => waitForInput(retries - 1), 100);
    };
    waitForInput();

    document.addEventListener('product-info:loaded', () => this.syncFromDOM());
  }

  private listenSellingPlanChanges(): void {
    const handler = (e: Event): void => {
      const t = e.target as HTMLInputElement | HTMLSelectElement | null;
      if (t && t.name === 'selling_plan') {
        setTimeout(() => this.syncPriceFromPriceContainer(), 150);
      }
    };
    document.addEventListener('change', handler, true);
    document.addEventListener('input', handler, true);

    // ReCharge updates the hidden selling_plan input programmatically (no change event).
    // Observe it via MutationObserver + poll (.value vs attribute diverge).
    const watchHiddenInput = (retries = 40): void => {
      const form = document.querySelector<HTMLFormElement>(
        'product-form form, form[data-type="add-to-cart-form"]'
      );
      const input = form?.querySelector<HTMLInputElement>('input[name="selling_plan"]');
      if (input) {
        const obs = new MutationObserver(() => {
          setTimeout(() => this.syncFromDOM(), 150);
        });
        obs.observe(input, { attributes: true, attributeFilter: ['value'] });
        this.rechargeObservers.push(obs);

        let lastValue = input.value;
        this.sellingPlanPollId = setInterval(() => {
          if (input.value !== lastValue) {
            lastValue = input.value;
            this.syncFromDOM();
          }
        }, 500);
        return;
      }
      if (retries > 0) setTimeout(() => watchHiddenInput(retries - 1), 250);
    };
    watchHiddenInput();
  }

  private observePriceContainer(): void {
    const waitForPrice = (retries = 40): void => {
      const priceContainer = document.querySelector("[id^='price-']");
      if (priceContainer) {
        this.mutationObserver = new MutationObserver(() => {
          this.syncPriceFromPriceContainer();
          this.syncAvailabilityFromSubmitButton();
        });
        this.mutationObserver.observe(priceContainer, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
        this.syncFromDOM();
        return;
      }
      if (retries > 0) setTimeout(() => waitForPrice(retries - 1), 200);
    };
    waitForPrice();
  }

  private syncFromDOM(): void {
    setTimeout(() => {
      this.syncVariantLabel();
      this.syncPriceFromPriceContainer();
      this.syncAvailabilityFromSubmitButton();
    }, 80);
  }

  private syncVariantLabel(): void {
    if (!this.variantEl) return;

    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;

    const script = variantSelects.querySelector('[data-selected-variant]');
    if (!script?.textContent) return;

    try {
      const variant = JSON.parse(script.textContent);
      if (variant.title) {
        this.variantEl.textContent = variant.title;
      }
    } catch {
      // ignore
    }
  }

  private syncPriceFromPriceContainer(): void {
    const priceContainer = document.querySelector("[id^='price-']");

    if (priceContainer) {
      const isOnSale = priceContainer.classList.contains('price--on-sale');

      if (isOnSale) {
        const saleSection = priceContainer.querySelector('.price__sale');
        if (saleSection) {
          const salePrice = saleSection.querySelector(
            '.price-item--sale, .price-item--last'
          ) as HTMLElement | null;
          const comparePrice = saleSection.querySelector(
            's.price-item--regular'
          ) as HTMLElement | null;

          if (salePrice && this.priceEl) {
            this.priceEl.textContent = salePrice.textContent?.trim() || '';
          }
          if (comparePrice && this.compareEl) {
            this.compareEl.textContent = comparePrice.textContent?.trim() || '';
            this.compareEl.style.display = '';
          }
        }
      } else {
        const regularSection = priceContainer.querySelector('.price__regular');
        if (regularSection) {
          const regularPrice = regularSection.querySelector(
            '.price-item--regular'
          ) as HTMLElement | null;
          if (regularPrice && this.priceEl) {
            this.priceEl.textContent = regularPrice.textContent?.trim() || '';
          }
        }
        if (this.compareEl) {
          this.compareEl.style.display = 'none';
        }
      }
      return;
    }

    this.syncPriceFromVariantData();
  }

  private static formatCents(cents: number): string {
    const w = window as any;
    if (w.Shopify && typeof w.Shopify.formatMoney === 'function') {
      const fmt = w.Shopify.money_format || w.Shopify.moneyFormat || '{{amount}}';
      return w.Shopify.formatMoney(cents, fmt);
    }
    const currency = w.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  private syncPriceFromVariantData(): void {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;

    const script = variantSelects.querySelector('[data-selected-variant]');
    if (!script?.textContent) return;

    let variant: any;
    try {
      variant = JSON.parse(script.textContent);
    } catch {
      return;
    }

    if (!variant?.price) return;

    const sellingPlanId = this.getSelectedSellingPlanId();
    let price = variant.price;
    let compareAtPrice = variant.compare_at_price;

    if (sellingPlanId && variant.selling_plan_allocations?.length) {
      const alloc = variant.selling_plan_allocations.find(
        (a: any) => String(a.selling_plan_id) === sellingPlanId ||
                     String(a.selling_plan_group_id) === sellingPlanId
      );
      if (alloc) {
        price = alloc.price;
        compareAtPrice = alloc.compare_at_price ?? variant.price;
      }
    }

    const isOnSale = compareAtPrice && compareAtPrice > price;

    if (this.priceEl) {
      this.priceEl.textContent = NADStickyAddToCart.formatCents(price);
      if (isOnSale && this.compareEl) {
        this.compareEl.textContent = NADStickyAddToCart.formatCents(compareAtPrice);
        this.compareEl.style.display = '';
      } else if (this.compareEl) {
        this.compareEl.style.display = 'none';
      }
    }
  }

  private observeRechargeWidget(): void {
    const observedRoots = new WeakSet<ShadowRoot>();
    let debounce: ReturnType<typeof setTimeout> | null = null;

    const scheduleSync = (): void => {
      if (debounce != null) clearTimeout(debounce);
      debounce = setTimeout(() => {
        debounce = null;
        this.syncFromDOM();
      }, 200);
    };

    const observeRoot = (sr: ShadowRoot): void => {
      if (observedRoots.has(sr)) return;
      observedRoots.add(sr);

      sr.addEventListener('change', scheduleSync);
      sr.addEventListener('click', scheduleSync);

      const obs = new MutationObserver((records) => {
        let hasNewNodes = false;
        for (const r of records) {
          if (r.type === 'childList' && r.addedNodes.length > 0) {
            hasNewNodes = true;
            break;
          }
        }
        if (hasNewNodes) discoverShadowRoots(sr);
        scheduleSync();
      });
      obs.observe(sr, { subtree: true, childList: true });
      this.rechargeObservers.push(obs);
    };

    const discoverShadowRoots = (root: Element | ShadowRoot): void => {
      root.querySelectorAll('*').forEach((el) => {
        if (el.shadowRoot) observeRoot(el.shadowRoot);
      });
    };

    const setup = (widget: Element): void => {
      if (widget.shadowRoot) {
        observeRoot(widget.shadowRoot);
        discoverShadowRoots(widget.shadowRoot);
      }
      const hostObs = new MutationObserver(() => {
        if (widget.shadowRoot) {
          observeRoot(widget.shadowRoot);
          discoverShadowRoots(widget.shadowRoot);
        }
      });
      hostObs.observe(widget, { childList: true, subtree: true });
      this.rechargeObservers.push(hostObs);
    };

    const waitForWidget = (retries = 40): void => {
      const widget = document.querySelector('recharge-subscription-widget');
      if (widget) {
        setup(widget);
        for (const delay of [300, 800, 2000]) {
          setTimeout(() => {
            if (widget.shadowRoot) {
              observeRoot(widget.shadowRoot);
              discoverShadowRoots(widget.shadowRoot);
            }
          }, delay);
        }
        return;
      }
      if (retries > 0) setTimeout(() => waitForWidget(retries - 1), 250);
    };
    waitForWidget();
  }

  private findSellingPlanInRoot(root: Element | ShadowRoot): string | null {
    const selects = root.querySelectorAll<HTMLSelectElement>('select[name="selling_plan"]');
    for (const sel of selects) {
      if (sel.value) return sel.value;
    }
    const radios = root.querySelectorAll<HTMLInputElement>('input[type="radio"][name="selling_plan"]');
    for (const r of radios) {
      if (r.checked && r.value) return r.value;
    }
    const hidden = root.querySelectorAll<HTMLInputElement>('input[name="selling_plan"]:not([type="radio"])');
    for (const inp of hidden) {
      if (inp.value) return inp.value;
    }
    return null;
  }

  private findSellingPlanInShadowRoots(host: Element): string | null {
    const stack: Element[] = [host];
    while (stack.length) {
      const el = stack.pop()!;
      if (el.shadowRoot) {
        const result = this.findSellingPlanInRoot(el.shadowRoot);
        if (result) return result;
        el.shadowRoot.querySelectorAll('*').forEach((node) => {
          if (node instanceof Element) stack.push(node);
        });
      }
    }
    return null;
  }

  private getSelectedSellingPlanId(): string | null {
    const roots: Element[] = [];
    const pi = document.querySelector('product-info');
    const tp = document.querySelector('.template-product');
    if (pi) roots.push(pi);
    if (tp) roots.push(tp);
    if (!roots.length) roots.push(document.body);

    for (const root of roots) {
      const result = this.findSellingPlanInRoot(root);
      if (result) return result;
    }

    const widget = document.querySelector('recharge-subscription-widget');
    if (widget) {
      const result = this.findSellingPlanInShadowRoots(widget);
      if (result) return result;
    }

    return null;
  }

  private syncAvailabilityFromSubmitButton(): void {
    if (!this.submitBtn || !this.buttonTextEl) return;

    const mainSubmit = document.querySelector<HTMLButtonElement>(
      'product-form .product-form__submit, product-form .product-form__submit--wrapped'
    );
    if (!mainSubmit) return;

    const isDisabled = mainSubmit.disabled || mainSubmit.getAttribute('aria-disabled') === 'true';

    if (isDisabled) {
      this.submitBtn.disabled = true;
      this.buttonTextEl.textContent = mainSubmit.textContent?.trim().replace(/\s+/g, ' ') || 'Sold out';
    } else {
      this.submitBtn.disabled = false;
      this.buttonTextEl.textContent = this.buttonLabel;
    }
  }

  public destroy(): void {
    this.setStickyOffsetProperty(false);
    this.observer?.disconnect();
    this.mutationObserver?.disconnect();
    this.rechargeObservers.forEach((obs) => obs.disconnect());
    this.rechargeObservers = [];
    if (this.sellingPlanPollId != null) {
      clearInterval(this.sellingPlanPollId);
      this.sellingPlanPollId = null;
    }
    if (this.countdownTimer != null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  static initializeAll(): void {
    document
      .querySelectorAll<HTMLElement>('#NadStickyAddToCart')
      .forEach((el) => {
        if (!(el as any)._nadComponentInstance) {
          (el as any)._nadComponentInstance = new NADStickyAddToCart(el);
        }
      });
  }
}
